/** Pure helpers for the input trailing-icon zone (no DOM). */

/**
 * Split icons into Deco (non-interactive) and Action (button) groups. An
 * explicit `deco` flag wins; otherwise an icon is an Action when it has an
 * `onClick` handler. The explicit flag lets a functional icon stay an Action
 * even when its handler was stripped (e.g. a disabled field), so it renders as
 * an inert disabled button instead of flipping to a decorative span.
 */
export function classifyIcons<T extends { onClick?: unknown; deco?: boolean }>(
  icons: readonly T[]
): { deco: T[]; actions: T[] } {
  const deco: T[] = [];
  const actions: T[] = [];
  for (const icon of icons) {
    const isDeco = icon.deco ?? icon.onClick == null;
    if (isDeco) deco.push(icon);
    else actions.push(icon);
  }
  return { deco, actions };
}

/**
 * Decide which actions stay inline vs. collapse into the overflow menu, given the
 * available pixel width for the action zone. If all actions fit, none overflow (no
 * trigger needed). Otherwise we reserve `triggerPx` for the overflow trigger and keep
 * as many leading actions inline as fit; array order = priority, so the LAST actions
 * collapse first.
 *
 * The trigger is itself one icon, so collapsing a single action into it swaps one icon
 * for another and saves no space. Overflow is therefore only used when it hides more
 * than one action; a lone surplus action stays inline. (Status/deco icons live in their
 * own reserved slot, not in `actions`, so they never count toward this.)
 */
export function splitActionsForWidth<T>(
  actions: readonly T[],
  availablePx: number,
  perActionPx: number,
  triggerPx: number
): { inline: T[]; overflow: T[] } {
  if (actions.length === 0) return { inline: [], overflow: [] };
  if (actions.length * perActionPx <= availablePx) {
    return { inline: [...actions], overflow: [] };
  }
  const inlineCount = Math.max(0, Math.floor((availablePx - triggerPx) / perActionPx));
  const overflow = actions.slice(inlineCount);
  // The trigger saves space only when it replaces more than one action.
  if (overflow.length <= 1) {
    return { inline: [...actions], overflow: [] };
  }
  return {
    inline: actions.slice(0, inlineCount),
    overflow,
  };
}

/**
 * Trailing chrome must never steal the typed/selected value. Deco (`%`, `€`)
 * is part of the value and keeps a small end pad. Lookup/goto/clear stay
 * visible on wide fields. Narrow (compact) fields drop those actions entirely
 * so "19 %" is not painted over by the clear X — F4 / click still open lookup.
 */
export const MIN_INPUT_TEXT_PX = 64;
export const TRAILING_ACTION_PX = 28;
/** Space between neighboring action targets inside a reserved text-cell rail. */
export const TRAILING_ACTION_GAP_PX = 4;
/** `%` / `€` glyphs are smaller than action hit-targets. */
export const TRAILING_DECO_PX = 18;
/**
 * ISO currency chips ("CHF", "USD") plus a gap so the amount cannot run
 * into the three-letter code. Wider than `TRAILING_DECO_PX` (€ / %).
 */
export const TRAILING_CURRENCY_CODE_PX = 40;
export const TRAILING_STATUS_PX = 20;
/** Hairline + margins between deco/status and actions (`trailing-divider`). */
export const TRAILING_DIVIDER_PX = 10;

/** Divider sits between deco/status and actions. An injected status slot
 * (table save wrapper) counts even when idle — it is what draws the hairline
 * in front of clear/lookup; padding must include it so text never crosses it. */
export function shouldShowTrailingDivider(
  actionCount: number,
  decoCount: number,
  hasStatusSlot: boolean
): boolean {
  return actionCount > 0 && (decoCount > 0 || hasStatusSlot);
}

/**
 * Whether lookup/goto/clear sit on the inline-start edge. An explicit prop
 * wins; then the table inject; then a DOM host with `align-end` (tests / cells
 * that did not wrap a provider).
 */
export function hostWantsStartActions(args: {
  prop?: 'start' | 'end';
  injected?: 'start' | 'end' | null;
  closestAlignEnd: boolean;
}): boolean {
  if (args.prop === 'start') return true;
  if (args.prop === 'end') return false;
  if (args.injected === 'start') return true;
  if (args.injected === 'end') return false;
  return args.closestAlignEnd;
}

/**
 * Mirror trailing action order for the leading zone. Trailing reads
 * `value … X` (lookup next to the value, clear outermost). The same array
 * on the left would paint `… X value`; reversing restores `X … value`.
 */
export function mirrorActionOrder<T>(actions: readonly T[]): T[] {
  return [...actions].reverse();
}

export function trailingAlwaysVisiblePx(decoPx: number, statusBusy: boolean): number {
  return Math.max(0, decoPx) + (statusBusy ? TRAILING_STATUS_PX : 0);
}

/** Sum of trailing deco widths (€ slot vs. wider ISO chips). */
export function decoIconsOccupyPx(icons: readonly { occupyPx?: number }[]): number {
  return icons.reduce((sum, icon) => sum + (icon.occupyPx ?? TRAILING_DECO_PX), 0);
}

export function capTrailingEndPadding(
  measuredPx: number,
  wrapperWidthPx: number,
  minTextPx = MIN_INPUT_TEXT_PX
): number {
  if (!Number.isFinite(measuredPx) || measuredPx <= 0) return 0;
  if (!Number.isFinite(wrapperWidthPx) || wrapperWidthPx <= 0) return measuredPx;
  return Math.min(measuredPx, Math.max(0, wrapperWidthPx - minTextPx));
}

/**
 * True when trailing chrome plus min text would overflow the field. Compact
 * fields unmount lookup/clear (CSS) so the value stays readable.
 */
export function isTrailingCompact(
  wrapperWidthPx: number,
  alwaysVisiblePx: number,
  actionCount: number,
  options: {
    perActionPx?: number;
    minTextPx?: number;
    showDivider?: boolean;
  } = {}
): boolean {
  if (!Number.isFinite(wrapperWidthPx) || wrapperWidthPx <= 0) return false;
  const {
    perActionPx = TRAILING_ACTION_PX,
    minTextPx = MIN_INPUT_TEXT_PX,
    showDivider = false,
  } = options;
  const visible = Number.isFinite(alwaysVisiblePx) ? Math.max(0, alwaysVisiblePx) : 0;
  const actionPx = Math.max(0, actionCount) * perActionPx;
  const dividerPx = showDivider && actionCount > 0 ? TRAILING_DIVIDER_PX : 0;
  return visible + actionPx + dividerPx + minTextPx > wrapperWidthPx;
}

/**
 * Padding-inline-end the input should reserve. Never measured from the
 * trailing group's `offsetWidth` (the idle status slot used to inflate it).
 *
 * Wide fields pad for every occupying action plus the divider so the value
 * cannot run through the hairline. Compact fields keep lookup/clear painted,
 * so they pad occupying actions too — shrinking below icon width stacked the
 * clear X on "19 %".
 */
export function reservedTrailingPaddingPx(args: {
  wrapperWidthPx: number;
  decoCount: number;
  /** Overrides `decoCount * TRAILING_DECO_PX` when deco icons differ in width. */
  decoPx?: number;
  /** Actions that occupy flex width (including reveal / visibility:hidden). */
  occupyingActionCount: number;
  /** Gap between neighboring actions. Defaults to zero for overlay-style cells. */
  actionGapPx?: number;
  statusBusy: boolean;
  compact: boolean;
  /** Hairline between deco/status and actions. */
  showDivider?: boolean;
  minTextPx?: number;
}): number {
  const decoPx = args.decoPx ?? Math.max(0, args.decoCount) * TRAILING_DECO_PX;
  const statusPx = args.statusBusy ? TRAILING_STATUS_PX : 0;
  const actionPx = Math.max(0, args.occupyingActionCount) * TRAILING_ACTION_PX;
  const actionGapPx =
    Math.max(0, args.occupyingActionCount - 1) * Math.max(0, args.actionGapPx ?? 0);
  if (args.compact) return decoPx + statusPx + actionPx + actionGapPx;
  const dividerPx = args.showDivider ? TRAILING_DIVIDER_PX : 0;
  return decoPx + statusPx + dividerPx + actionPx + actionGapPx;
}
